import { useEffect, useRef, useCallback, useState } from "react";
import { io } from "socket.io-client";

/**
 * Custom hook to manage WebSocket connection for mobile image uploads
 * Eliminates the need for multiple refs by properly managing socket lifecycle
 * and event handler dependencies
 *
 * @param {Object} config - Configuration object
 * @param {string} config.sessionId - Current mobile upload session ID
 * @param {string} config.sampleId - Sample ID for this upload session
 * @param {function} config.onImageReceived - Callback when mobile image is received
 * @param {function} config.onSessionExpired - Callback when session expires
 * @param {function} config.onPinReceived - Callback when PIN is generated (desktop only)
 * @param {number} config.imageCount - Current number of images (for sync)
 * @returns {Object} Socket instance and connection state
 */
export const useMobileUploadSocket = ({
  sessionId,
  sampleId,
  onImageReceived,
  onSessionExpired,
  onPinLockout,
  onPinReceived,
  imageCount = 0,
}) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const isConnectedRef = useRef(false);
  const hasRegisteredRef = useRef(false);
  const expiredSessionIdsRef = useRef(new Set());

  const markAndExpireSession = useCallback(
    (eventSessionId) => {
      if (!sessionId) {
        return;
      }

      if (eventSessionId && eventSessionId !== sessionId) {
        console.log("Ignoring stale session expiry event", {
          eventSessionId,
          activeSessionId: sessionId,
        });
        return;
      }

      const key = sessionId;
      if (expiredSessionIdsRef.current.has(key)) {
        return;
      }
      expiredSessionIdsRef.current.add(key);
      console.log("Session expired", { sessionId: key });
      onSessionExpired();
    },
    [onSessionExpired, sessionId],
  );

  // Create stable callbacks using useCallback to prevent unnecessary recreations
  const handleMobileImageUploaded = useCallback(
    (data) => {
      console.log("Received mobile image:", data);
      const { imageUrl, imageKey, fileName } = data;

      const newImage = {
        url: imageUrl,
        key: imageKey,
        preview: imageUrl,
        name: fileName || "Mobile Upload",
      };

      onImageReceived(newImage);
    },
    [onImageReceived],
  );

  const handleSessionExpired = useCallback(
    (data) => {
      markAndExpireSession(data?.sessionId);
    },
    [markAndExpireSession],
  );

  const handlePinLockout = useCallback(
    (data) => {
      console.log("PIN lockout received:", data);
      if (onPinLockout) {
        onPinLockout(data);
      }
      markAndExpireSession(data?.sessionId);
    },
    [onPinLockout, markAndExpireSession],
  );

  const handleSessionCreated = useCallback(
    (data) => {
      console.log("Session created with PIN:", data.sessionId);
      if (onPinReceived && data.pin) {
        onPinReceived(data.pin);
      }
    },
    [onPinReceived],
  );

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(
      process.env.REACT_APP_API_URL || window.location.origin,
      {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        withCredentials: true,
      },
    );

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("Mobile upload socket connected");
      isConnectedRef.current = true;
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      console.log("Mobile upload socket disconnected");
      isConnectedRef.current = false;
      setIsConnected(false);
      hasRegisteredRef.current = false;
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []); // Only initialize once

  // On unmount or sessionId change, send abandon signal to clean up staged uploads.
  // isConnected is intentionally tracked via isConnectedRef (not state) so this
  // cleanup only fires on genuine unmount/sessionId-change, not on every
  // disconnect/reconnect cycle. A stale isConnected=true closure would otherwise
  // buffer an abandon-session on every brief disconnect, expiring the session before
  // the socket notification can deliver the uploaded image to the desktop.
  // socketRef/isConnectedRef/hasRegisteredRef are stable ref objects — including them
  // in deps satisfies exhaustive-deps without triggering re-runs.
  useEffect(() => {
    return () => {
      if (
        socketRef.current &&
        sessionId &&
        isConnectedRef.current &&
        hasRegisteredRef.current
      ) {
        console.log(
          `[useMobileUploadSocket cleanup] Abandoning session ${sessionId}`,
        );
        socketRef.current.emit("abandon-session", {
          sessionId,
          reason: "hook-unmount",
        });
      }
    };
  }, [sessionId, socketRef, isConnectedRef, hasRegisteredRef]);

  // Set up event listeners after socket is initialized/connected
  useEffect(() => {
    if (!socketRef.current) return;

    socketRef.current.on("mobile-image-uploaded", handleMobileImageUploaded);
    socketRef.current.on("session-expired", handleSessionExpired);
    socketRef.current.on("mobile-pin-lockout", handlePinLockout);
    socketRef.current.on("session-created", handleSessionCreated);

    return () => {
      if (socketRef.current) {
        socketRef.current.off(
          "mobile-image-uploaded",
          handleMobileImageUploaded,
        );
        socketRef.current.off("session-expired", handleSessionExpired);
        socketRef.current.off("mobile-pin-lockout", handlePinLockout);
        socketRef.current.off("session-created", handleSessionCreated);
      }
    };
  }, [
    handleMobileImageUploaded,
    handleSessionExpired,
    handlePinLockout,
    handleSessionCreated,
    isConnected,
  ]);

  // Register desktop session when sessionId changes or when socket connects.
  // imageCount is intentionally excluded: it is already synced to the server via
  // the dedicated desktop-image-count-update effect below. Including it here caused
  // a spurious re-registration after every received image, which reset the server-side
  // session object mid-upload and interfered with concurrent HTTP POSTs.
  useEffect(() => {
    if (!socketRef.current || !sessionId || !isConnected) {
      console.log(
        "Cannot register - socket:",
        !!socketRef.current,
        "sessionId:",
        sessionId,
        "connected:",
        isConnected,
      );
      return;
    }

    console.log("Registering desktop with sessionId:", sessionId);
    socketRef.current.emit("register-desktop", {
      sessionId,
      currentImageCount: imageCount,
      sampleId,
    });
    hasRegisteredRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sampleId, isConnected]);

  // Sync image count when it changes
  useEffect(() => {
    if (
      !socketRef.current ||
      !sessionId ||
      !isConnected ||
      !hasRegisteredRef.current
    )
      return;

    socketRef.current.emit("desktop-image-count-update", {
      sessionId,
      currentImageCount: imageCount,
    });
  }, [sessionId, imageCount, isConnected]);

  return {
    socket: socketRef.current,
    isConnected,
  };
};
